import { useState, useEffect } from 'react';
import { useLocation, Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  Calendar, User, FileText, IndianRupee, Upload,
  Check, X, Eye, ArrowLeft, Clock, Phone, Mail,
  FlaskConical, Tag, AlertCircle, CheckCircle2
} from 'lucide-react';
import {
  getSelectedAppointmentData, cancelLabAppointments,
  completeLabAppointments, uploadReports,
  getAppointmentReports, deleteAppointmentReport
} from '../../apiCalls/appointments';
import moment from "moment/moment";
import parse from 'html-react-parser';
import { toast } from "react-toastify";
import { reportsUrl } from '../../requestMethods';

/* ── Status config ─────────────────────────── */
const STATUS = {
  booked:          { label: 'Booked',            bg: 'var(--info-light)',    color: '#1e40af' },
  paid:            { label: 'Paid',              bg: 'var(--success-light)', color: '#065f46' },
  completed:       { label: 'Completed',         bg: 'var(--purple-light)',  color: '#5b21b6' },
  cancelled:       { label: 'Cancelled',         bg: 'var(--danger-light)',  color: '#991b1b' },
  cancelledbyuser: { label: 'Cancelled by User', bg: 'var(--warning-light)', color: '#92400e' },
};

const getStatus = (s = '') => STATUS[s.toLowerCase().replace(/\s+/g, '')] || STATUS.booked;

/* ── Small reusables ──────────────────────── */
const InfoItem = ({ label, value, icon, full }) => (
  <div style={{ gridColumn: full ? 'span 2' : 'span 1' }}>
    <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: '0.3rem', display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
      {icon} {label}
    </div>
    <div style={{ fontSize: '0.9rem', fontWeight: 600, color: 'var(--gray-800)' }}>
      {value || <span style={{ color: 'var(--gray-300)', fontWeight: 400 }}>—</span>}
    </div>
  </div>
);

const SectionCard = ({ icon, title, children, action }) => (
  <div className="form-card" style={{ marginBottom: 0 }}>
    <div className="form-card-header">
      <h5 className="form-card-title">{icon} {title}</h5>
      {action}
    </div>
    <div className="form-card-body">{children}</div>
  </div>
);

/* ── Main component ───────────────────────── */
const AppointmentDetails = () => {
  const location   = useLocation();
  const navigate   = useNavigate();
  const dispatch   = useDispatch();
  const appointId  = location.pathname.split("/")[3];

  const [appointmentData, setAppointmentData] = useState({});
  const [reportName,      setReportName]      = useState("");
  const [uploadedFiles,   setUploadedFiles]   = useState([]);
  const [showModal,       setShowModal]       = useState(false);
  const [confirmAction,   setConfirmAction]   = useState(null); // 'complete' | 'cancel'
  const [actionLoading,   setActionLoading]   = useState(false);

  const reportsData = useSelector((state) => state.reports.reports);

  useEffect(() => {
    getSelectedAppointmentData(appointId).then(setAppointmentData);
    getAppointmentReports(dispatch, appointId);
  }, [appointId]);

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files).map(f => ({
      name: f.name, size: (f.size / 1024).toFixed(2) + ' KB',
      uploadDate: new Date().toLocaleDateString(), file: f,
    }));
    setUploadedFiles(files);
  };

  const removeReport = async (reportId) => {
    await deleteAppointmentReport(reportId);
    getAppointmentReports(dispatch, appointId);
  };

  const uploadReportData = async () => {
    if (!reportName.trim()) { toast.error("Report name is required"); return; }
    if (!uploadedFiles.length) { toast.error("Please select at least one file"); return; }
    const data = await uploadReports(
      uploadedFiles, appointId, reportName,
      appointmentData?.userId, appointmentData?.testId?._id, appointmentData.labId
    );
    toast.success(data.message);
    setShowModal(false);
    setReportName("");
    setUploadedFiles([]);
    getAppointmentReports(dispatch, appointId);
  };

  const handleConfirmedAction = async () => {
    setActionLoading(true);
    if (confirmAction === 'complete') await completeLabAppointments(appointId);
    if (confirmAction === 'cancel')   await cancelLabAppointments(appointId);
    setActionLoading(false);
    setConfirmAction(null);
    getSelectedAppointmentData(appointId).then(setAppointmentData);
  };

  const statusMeta  = getStatus(appointmentData?.status);
  const isCompleted = appointmentData?.status === 'completed';
  const isCancelled = appointmentData?.status?.includes('cancel');

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

      {/* ── Page header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem' }}>
          <button className="app-btn app-btn--ghost app-btn--sm" onClick={() => navigate('/appointments/appointment-list')}>
            <ArrowLeft size={14} /> Back
          </button>
          <div>
            <h4 style={{ margin: 0, fontSize: '1.15rem', fontWeight: 700, color: 'var(--gray-900)' }}>
              Appointment Details
            </h4>
            <div style={{ fontSize: '0.75rem', color: 'var(--gray-400)', marginTop: 2 }}>
              ID: {appointId}
            </div>
          </div>
        </div>

        {/* Status badge */}
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
          padding: '0.4rem 1rem', borderRadius: '9999px',
          fontSize: '0.75rem', fontWeight: 700, letterSpacing: '0.5px',
          textTransform: 'uppercase', background: statusMeta.bg, color: statusMeta.color,
          boxShadow: `0 2px 8px ${statusMeta.bg}`,
        }}>
          <i className="bi bi-circle-fill" style={{ fontSize: 6 }} />
          {statusMeta.label}
        </span>
      </div>

      {/* ── Two-column grid ── */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '1.25rem' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0,2fr) minmax(0,1fr)', gap: '1.25rem', alignItems: 'start' }}>

          {/* ── LEFT column ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* Appointment Info */}
            <SectionCard icon={<Calendar size={15} />} title="Appointment Information">
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.1rem' }}>
                <InfoItem
                  label="Appointment Date" icon={<Calendar size={11} />}
                  value={moment(appointmentData?.appointmentTime).format('DD MMM YYYY')}
                />
                <InfoItem
                  label="Appointment Time" icon={<Clock size={11} />}
                  value={moment(appointmentData?.appointmentTime).format('hh:mm A')}
                />
                <InfoItem
                  label="Booked On" icon={<Calendar size={11} />}
                  value={appointmentData?.createdat
                    ? `${moment(appointmentData.createdat).format('DD MMM YYYY')} at ${moment(appointmentData.createdat).format('hh:mm A')}`
                    : null}
                />
                <InfoItem
                  label="Status" icon={<CheckCircle2 size={11} />}
                  value={<span style={{ color: statusMeta.color, fontWeight: 700 }}>{statusMeta.label}</span>}
                />
              </div>
            </SectionCard>

            {/* Patient Info */}
            <SectionCard icon={<User size={15} />} title="Patient Information">
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.875rem', marginBottom: '1.1rem', paddingBottom: '1rem', borderBottom: '1px solid var(--gray-50)' }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%', flexShrink: 0,
                  background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                  color: '#fff', fontSize: '1rem', fontWeight: 700,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  boxShadow: '0 2px 8px rgba(129,102,244,0.3)',
                }}>
                  {(appointmentData?.appointerName || 'P')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--gray-900)' }}>
                    {appointmentData?.appointerName}
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>Patient</div>
                </div>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.1rem' }}>
                <InfoItem label="Contact" icon={<Phone size={11} />} value={appointmentData?.phoneNumber} />
                <InfoItem label="Email"   icon={<Mail  size={11} />} value={appointmentData?.userDetails?.email} />
              </div>
            </SectionCard>

            {/* Test Details */}
            <SectionCard icon={<FlaskConical size={15} />} title="Test Details">
              {/* Test name highlight */}
              <div style={{
                background: 'var(--primary-light)', borderRadius: 'var(--radius-md)',
                padding: '0.875rem 1rem', marginBottom: '1rem',
                display: 'flex', alignItems: 'center', gap: '0.75rem',
              }}>
                <div style={{
                  width: 36, height: 36, borderRadius: 'var(--radius-md)',
                  background: 'var(--primary-muted)', display: 'flex',
                  alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                }}>
                  <FlaskConical size={16} style={{ color: 'var(--primary)' }} />
                </div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: '0.95rem', color: 'var(--gray-900)' }}>
                    {appointmentData?.testId?.subCategoryName || '—'}
                  </div>
                  <div style={{ fontSize: '0.72rem', color: 'var(--gray-500)', marginTop: 2 }}>Test Name</div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
                <InfoItem label="Duration" icon={<Clock size={11} />}
                  value={appointmentData?.duration ? `${appointmentData.duration} mins` : null}
                />
                <InfoItem label="Category" icon={<Tag size={11} />}
                  value={appointmentData?.testId?.categoryName}
                />
              </div>

              {appointmentData?.testId?.description && (
                <div>
                  <div style={{ fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.5px', color: 'var(--gray-400)', marginBottom: '0.5rem' }}>
                    Description
                  </div>
                  <div style={{
                    fontSize: '0.855rem', color: 'var(--gray-600)', lineHeight: 1.7,
                    background: 'var(--gray-50)', borderRadius: 'var(--radius-md)',
                    padding: '0.875rem', border: '1px solid var(--gray-100)',
                  }}>
                    {parse(appointmentData.testId.description)}
                  </div>
                </div>
              )}
            </SectionCard>

            {/* Lab Reports */}
            <SectionCard
              icon={<Upload size={15} />}
              title="Lab Reports"
              action={
                <button
                  className="app-btn app-btn--outline-primary app-btn--sm"
                  onClick={() => setShowModal(true)}
                >
                  <Upload size={13} /> Upload Report
                </button>
              }
            >
              {reportsData.length === 0 ? (
                <div style={{ textAlign: 'center', padding: '2.5rem 1rem' }}>
                  <div style={{
                    width: 52, height: 52, borderRadius: '50%', background: 'var(--gray-100)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 0.75rem',
                  }}>
                    <FileText size={22} style={{ color: 'var(--gray-300)' }} />
                  </div>
                  <div style={{ fontSize: '0.875rem', fontWeight: 600, color: 'var(--gray-600)', marginBottom: 4 }}>
                    No reports uploaded yet
                  </div>
                  <div style={{ fontSize: '0.78rem', color: 'var(--gray-400)' }}>
                    Upload PDF or image reports for this appointment
                  </div>
                </div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                  {reportsData.map((file, i) => (
                    <div key={i} style={{
                      display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '0.875rem', background: 'var(--gray-50)',
                      borderRadius: 'var(--radius-md)', border: '1px solid var(--gray-100)',
                      transition: 'background 0.15s',
                    }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--primary-light)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'var(--gray-50)'}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flex: 1, minWidth: 0 }}>
                        <div style={{
                          width: 36, height: 36, borderRadius: 'var(--radius-md)',
                          background: 'var(--primary-muted)', display: 'flex',
                          alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                        }}>
                          <FileText size={16} style={{ color: 'var(--primary)' }} />
                        </div>
                        <div style={{ minWidth: 0 }}>
                          <div style={{ fontWeight: 600, color: 'var(--gray-800)', fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                            {file.reportName}
                          </div>
                          <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 2 }}>
                            Uploaded {moment(file.createdAt).format('DD MMM YYYY')}
                          </div>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: '0.5rem', flexShrink: 0, marginLeft: '0.75rem' }}>
                        <Link target="_blank" to={`${reportsUrl}/${file.reportFiles?.[0]?.fileUrl}`}>
                          <button className="app-btn app-btn--ghost app-btn--sm" title="View report">
                            <Eye size={13} />
                          </button>
                        </Link>
                        <button
                          className="app-btn app-btn--sm"
                          style={{ background: 'var(--danger-light)', color: '#dc2626', border: '1px solid #fca5a5' }}
                          onClick={() => removeReport(file?._id)}
                          title="Delete report"
                        >
                          <X size={13} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </SectionCard>
          </div>

          {/* ── RIGHT column ── */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>

            {/* Payment */}
            <SectionCard icon={<IndianRupee size={15} />} title="Payment">
              {/* Amount highlight */}
              <div style={{
                background: 'linear-gradient(135deg, var(--primary) 0%, var(--primary-dark) 100%)',
                borderRadius: 'var(--radius-md)', padding: '1rem',
                marginBottom: '1rem', textAlign: 'center',
              }}>
                <div style={{ fontSize: '0.72rem', fontWeight: 600, color: 'rgba(255,255,255,0.7)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 6 }}>
                  Total Charges
                </div>
                <div style={{ fontSize: '1.75rem', fontWeight: 800, color: '#fff', letterSpacing: '-0.5px' }}>
                  ₹{appointmentData?.charges?.toFixed(2) || '0.00'}
                </div>
              </div>

              {/* Payment rows */}
              {[
                { label: 'Payment Status', value: <span style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '0.2rem 0.65rem', borderRadius: '9999px',
                    fontSize: '0.7rem', fontWeight: 700,
                    background: statusMeta.bg, color: statusMeta.color,
                  }}>{statusMeta.label}</span>
                },
                { label: 'Paid On', value: appointmentData?.paymentId
                    ? `${moment(appointmentData.paymentId.createdAt).format('DD MMM YYYY')} at ${moment(appointmentData.paymentId.createdAt).format('hh:mm A')}`
                    : null
                },
              ].map(({ label, value }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.65rem 0', borderBottom: '1px solid var(--gray-50)' }}>
                  <span style={{ fontSize: '0.8rem', color: 'var(--gray-500)' }}>{label}</span>
                  <span style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--gray-800)' }}>
                    {value || <span style={{ color: 'var(--gray-300)', fontWeight: 400 }}>—</span>}
                  </span>
                </div>
              ))}
            </SectionCard>

            {/* Quick Actions */}
            <SectionCard icon={<CheckCircle2 size={15} />} title="Quick Actions">
              <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                <button
                  className="app-btn app-btn--success app-btn--full"
                  onClick={() => setConfirmAction('complete')}
                  disabled={isCompleted || isCancelled}
                  style={{ opacity: (isCompleted || isCancelled) ? 0.5 : 1, cursor: (isCompleted || isCancelled) ? 'not-allowed' : 'pointer' }}
                >
                  <Check size={15} />
                  {isCompleted ? 'Already Completed' : 'Mark as Completed'}
                </button>
                <button
                  className="app-btn app-btn--danger app-btn--full"
                  onClick={() => setConfirmAction('cancel')}
                  disabled={isCancelled || isCompleted}
                  style={{ opacity: (isCancelled || isCompleted) ? 0.5 : 1, cursor: (isCancelled || isCompleted) ? 'not-allowed' : 'pointer' }}
                >
                  <X size={15} />
                  {isCancelled ? 'Already Cancelled' : 'Cancel Appointment'}
                </button>
              </div>
            </SectionCard>

          </div>
        </div>
      </div>

      {/* ── Upload Report Modal ── */}
      {showModal && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1050,
          background: 'rgba(15,10,36,0.5)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}>
          <div style={{
            background: 'var(--white)', borderRadius: 'var(--radius-xl)',
            width: '100%', maxWidth: 480,
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)', overflow: 'hidden',
          }}>
            {/* Modal header */}
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '1rem 1.25rem', background: 'var(--primary-light)',
              borderBottom: '1px solid var(--gray-100)',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                <div style={{ width: 32, height: 32, borderRadius: 'var(--radius-md)', background: 'var(--primary-muted)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                  <Upload size={15} style={{ color: 'var(--primary)' }} />
                </div>
                <span style={{ fontSize: '0.9rem', fontWeight: 700, color: 'var(--gray-900)' }}>Upload Lab Report</span>
              </div>
              <button
                onClick={() => { setShowModal(false); setReportName(''); setUploadedFiles([]); }}
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--gray-400)', padding: '0.25rem', borderRadius: 'var(--radius)' }}
              >
                <X size={18} />
              </button>
            </div>

            {/* Modal body */}
            <div style={{ padding: '1.5rem' }}>
              <div className="field">
                <label className="field-label">
                  <FileText size={12} /> Report Name <span className="req">*</span>
                </label>
                <input
                  type="text"
                  className="field-input"
                  placeholder="e.g. Blood Test Report"
                  value={reportName}
                  onChange={e => setReportName(e.target.value)}
                />
              </div>

              <div className="field" style={{ marginTop: '1rem' }}>
                <label className="field-label">
                  <Upload size={12} /> Report Files <span className="req">*</span>
                </label>
                <div style={{
                  border: `2px dashed ${uploadedFiles.length ? 'var(--primary)' : 'var(--gray-200)'}`,
                  borderRadius: 'var(--radius-md)', padding: '1.25rem',
                  background: uploadedFiles.length ? 'var(--primary-light)' : 'var(--gray-50)',
                  textAlign: 'center', transition: 'all 0.18s',
                }}>
                  <input
                    type="file" multiple accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleFileUpload}
                    style={{ display: 'none' }} id="report-file-input"
                  />
                  <label htmlFor="report-file-input" style={{ cursor: 'pointer' }}>
                    {uploadedFiles.length ? (
                      <div>
                        <CheckCircle2 size={24} style={{ color: 'var(--success)', marginBottom: '0.5rem' }} />
                        <div style={{ fontWeight: 600, color: 'var(--gray-800)', fontSize: '0.875rem' }}>
                          {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} selected
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 2 }}>Click to change</div>
                      </div>
                    ) : (
                      <div>
                        <Upload size={24} style={{ color: 'var(--gray-400)', marginBottom: '0.5rem' }} />
                        <div style={{ fontWeight: 600, color: 'var(--gray-600)', fontSize: '0.875rem' }}>
                          Click to browse files
                        </div>
                        <div style={{ fontSize: '0.72rem', color: 'var(--gray-400)', marginTop: 2 }}>
                          PDF, JPG, PNG accepted
                        </div>
                      </div>
                    )}
                  </label>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.25rem' }}>
                <button className="app-btn app-btn--primary" style={{ flex: 1 }} onClick={uploadReportData}>
                  <Upload size={14} /> Upload Report
                </button>
                <button
                  className="app-btn app-btn--ghost"
                  onClick={() => { setShowModal(false); setReportName(''); setUploadedFiles([]); }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Confirm action modal ── */}
      {confirmAction && (
        <div style={{
          position: 'fixed', inset: 0, zIndex: 1050,
          background: 'rgba(15,10,36,0.5)', backdropFilter: 'blur(3px)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '1rem',
        }}>
          <div style={{
            background: 'var(--white)', borderRadius: 'var(--radius-xl)',
            width: '100%', maxWidth: 400,
            boxShadow: '0 20px 50px rgba(0,0,0,0.2)', padding: '2rem', textAlign: 'center',
          }}>
            <div style={{
              width: 56, height: 56, borderRadius: '50%', margin: '0 auto 1rem',
              background: confirmAction === 'complete' ? 'var(--success-light)' : 'var(--danger-light)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              {confirmAction === 'complete'
                ? <Check size={24} style={{ color: 'var(--success)' }} />
                : <AlertCircle size={24} style={{ color: 'var(--danger)' }} />
              }
            </div>
            <h4 style={{ fontSize: '1rem', fontWeight: 700, color: 'var(--gray-900)', marginBottom: '0.5rem' }}>
              {confirmAction === 'complete' ? 'Mark as Completed?' : 'Cancel Appointment?'}
            </h4>
            <p style={{ fontSize: '0.855rem', color: 'var(--gray-500)', marginBottom: '1.5rem !important' }}>
              {confirmAction === 'complete'
                ? 'This will mark the appointment as completed. This action cannot be undone.'
                : 'Are you sure you want to cancel this appointment?'
              }
            </p>
            <div style={{ display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
              <button
                className={`app-btn ${confirmAction === 'complete' ? 'app-btn--success' : 'app-btn--danger'}`}
                onClick={handleConfirmedAction}
                disabled={actionLoading}
              >
                {actionLoading ? 'Processing…' : confirmAction === 'complete' ? 'Yes, Complete' : 'Yes, Cancel'}
              </button>
              <button className="app-btn app-btn--ghost" onClick={() => setConfirmAction(null)}>
                Go Back
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentDetails;
